/**
 * PrimaryKeyDropFormBody — drop-PK form body. The constraint name is required
 * on mssql / pgsql / oracle (drop-by-name) and ignored on mysql (DROP PRIMARY
 * KEY targets the implicit constraint). Cascade is exposed on pgsql + oracle.
 *
 * sqlite is gated upstream by `isDropPrimaryKeySupported`; included as a
 * defensive runtime guard.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B1.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  commitDropPrimaryKey,
  isCascadeSupported,
  requiresConstraintName,
  validateDropPrimaryKeyForEngine,
} from '../../edit/edits/dropPrimaryKey';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

export interface PrimaryKeyDropFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function PrimaryKeyDropFormBody(props: PrimaryKeyDropFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showName = requiresConstraintName(engine);
  const showCascade = isCascadeSupported(engine);

  const [constraintName, setConstraintName] = useState('');
  const [cascade, setCascade] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    setError(null);
    const args = {
      constraintName: showName ? constraintName.trim() : undefined,
      cascade: showCascade ? cascade : undefined,
    };
    const validationError = validateDropPrimaryKeyForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitDropPrimaryKey(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Drop primary key</div>
        <FormEngineChip engine={engine} />
      </div>

      {showName ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-name`} className="text-xs">
            Constraint name <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-name`}
            data-testid={`${testId}-name`}
            value={constraintName}
            onChange={(e) => setConstraintName(e.target.value)}
            placeholder="pk_table"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : (
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-no-name-note`}
        >
          MySQL: DROP PRIMARY KEY targets the implicit constraint — no name required.
        </div>
      )}

      {showCascade ? (
        <label
          className="flex items-center gap-2 text-xs"
          data-testid={`${testId}-cascade-label`}
        >
          <Checkbox
            checked={cascade}
            onCheckedChange={(checked) => setCascade(checked === true)}
            data-testid={`${testId}-cascade`}
          />
          <span>CASCADE — drop dependent constraints</span>
        </label>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Dropping…' : 'Drop'}
        </Button>
      </div>
    </div>
  );
}
